import AllInPricing from "@shared/modules/financials/components/AllInPricing/AllInPricing.web";
import InventoryBadge from "@ui-application/data-display/badges/InventoryBadge/InventoryBadge.web";
import StatusBadge from "@ui-application/data-display/badges/StatusBadge/StatusBadge.web";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import breakpoints from "@shared/theme/props/breakpoints";
import { getItemPrice } from "@shared/modules/financials/utils/price-utils";
import { useTranslation } from "@shared/utils/internationalization";
import { isOnSaleTicketType } from "@shared/modules/ticket-types/utils/ticket-type-utils";
import QuantitySelect from "@ui-application/form/QuantitySelect/QuantitySelect.web";
import SaleStartsOnBadge from "@ui-application/data-display/badges/SaleStartsOnBadge/SaleStartsOnBadge.web";
import { useMediaQuery } from "@shared/hooks/useMediaQuery";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Box from "@ui-kit/layout/Box/Box.web";
import ItemPasswordEntry from "@shared/modules/events/components/ItemPasswordEntry/ItemPasswordEntry.web";
import ReadMore from "@ui-kit/typography/ReadMore/ReadMore.web";
import { useSelectionItem } from "./useSelectionItem";
import { SelectionItemType } from "../ItemSelectModal.props";

const { sm } = breakpoints;

const SelectionItem = (props: SelectionItemType) => {
  const { t } = useTranslation("seating");

  // this is required to prevent QuantitySelect from appearing twice in the DOM
  const [isSmallerThanSm] = useMediaQuery(`(max-width: ${sm - 1}px)`);

  const {
    item,
    seatingEvent,
    ticketAuthArray,
    updateTicketAuthArray,
    venueData,
    itemSelectData,
    hidePrice,
  } = props;

  const {
    allInPricing,
    maxPurchaseLimit,
    minPurchaseLimit,
    canShowSelectButton,
    showPasswordForm,
    soldOutAndNotWaitlisted,
    isDisabledSelector,
    paymentType,
    purchaseSourcePlatform,
    currentSelectedAmount,
    isSeat,
    isLocation,
    selectSeatCallback,
    selectLocationCallback,
  } = useSelectionItem(props);

  return (
    <Flex
      flexDirection="column"
      gap="xs"
      p="md"
      w="full"
      border="1px solid"
      borderColor="gray.400"
      rounded={{ base: "lg", sm: "lg" }}
    >
      <Flex alignItems="center" gap="xs">
        {/* Display sale starts on badge */}
        <SaleStartsOnBadge
          saleStartsOn={item.sale_starts_on}
          isOnSale={isOnSaleTicketType(
            item,
            item.bypass_sale_starts_on_box_office
          )}
          timezone={seatingEvent.timezone}
        />
        {/* Display sold out badge */}
        {soldOutAndNotWaitlisted && (
          <StatusBadge status="error" variant="solid">
            <Text fontSize="xs" textTransform="uppercase" color="inherit">
              {t("Sold Out", { ns: "common" })}
            </Text>
          </StatusBadge>
        )}
        {/* Display low inventory or remaining tickets badge */}
        <InventoryBadge
          tt={item}
          eventData={seatingEvent}
          venueData={venueData}
        />
        {/* TODO: Waitlists are not currently supported in the box office. Display waitlist badge when they are enabled */}
        {/* {soldOutAndWaitlisted && (
          <Tag
            fontSize="14px"
            fontWeight="bold"
            backgroundColor="primary.100"
            color="primary.600"
          >
            <Text fontSize="12px" textTransform="uppercase" color="inherit">
              {t("Waitlist", { ns: "common" })}
            </Text>
          </Tag>
        )} */}
      </Flex>
      <Flex
        w="full"
        alignItems="center"
        opacity={soldOutAndNotWaitlisted ? "0.3" : "1.0"}
      >
        {/* Display item's color (colorFill) */}
        <Text
          minWidth="element.xl"
          minHeight="element.xl"
          borderRadius="50%"
          marginRight="md"
          style={{
            background: item.colorFill,
            position: "relative",
          }}
        />

        <Flex
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          width="100%"
        >
          <Flex direction="column">
            {/* Item name */}
            <Text fontSize="md" fontWeight="bold" color="font.dark">
              {item.name}
            </Text>
            {!hidePrice &&
              venueData.currency &&
              item.price &&
              item.fees_pricing_info && (
                <Text fontSize="sm" color="font.dark">
                  <CurrencyFormat
                    value={getItemPrice(
                      item?.price?.toString(),
                      allInPricing,
                      item?.fees_pricing_info,
                      paymentType,
                      purchaseSourcePlatform
                    )}
                    displayType="text"
                    currency={venueData.currency}
                    includeSuffix
                  />
                </Text>
              )}
            {!hidePrice && (
              <AllInPricing
                feesPricingInfo={item?.fees_pricing_info}
                hasAllInPricing={allInPricing}
                price={item?.price?.toString()}
                currency={venueData.currency}
              />
            )}
            {/* Always show item-level purchase limits on the box office */}
            {item.minimum_purchase_limit && (
              <Text fontSize="sm" color="secondary.400">{`*${t(
                "Minimum {{count}} items required",
                {
                  count: item.minimum_purchase_limit,
                }
              )}`}</Text>
            )}
          </Flex>
          {/* Show quantity button/dropdown on same line as item name */}
          {itemSelectData && canShowSelectButton && !isSmallerThanSm && (
            <>
              <Box minWidth="element.2xl" />
              <Flex
                width="element.9xl"
                direction="row"
                justifyContent="flex-end"
              >
                <QuantitySelect
                  isDisabled={isDisabledSelector}
                  buttonProps={{
                    showButton: isSeat,
                    callbackFunc: selectSeatCallback,
                  }}
                  incrementorProps={{
                    showIncrementor: isLocation,
                    currentSelectedAmount,
                    min: minPurchaseLimit,
                    max: maxPurchaseLimit,
                    callbackFunc: selectLocationCallback,
                  }}
                />
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
      {/* Show quantity button/dropdown on a new line */}
      {itemSelectData && canShowSelectButton && isSmallerThanSm && (
        <Flex pt="xs" width="100%" direction="row" justifyContent="flex-end">
          <QuantitySelect
            isDisabled={isDisabledSelector}
            buttonProps={{
              showButton: isSeat,
              callbackFunc: selectSeatCallback,
            }}
            incrementorProps={{
              showIncrementor: isLocation,
              currentSelectedAmount,
              min: minPurchaseLimit,
              max: maxPurchaseLimit,
              callbackFunc: selectLocationCallback,
            }}
          />
        </Flex>
      )}
      {/* Show password form only if item isn't sold out and the map isn't for memberships */}
      {showPasswordForm && (
        <ItemPasswordEntry
          item={item}
          slugToCheck={seatingEvent.slug}
          authArray={ticketAuthArray}
          updateAuthArray={updateTicketAuthArray}
        />
      )}
      {/* Show item description */}
      {item.description && (
        <ReadMore
          descriptionHtml={item.description}
          openReadMoreText={t("Read More", { ns: "common" })}
          closeReadMoreText={t("Show Less", { ns: "common" })}
        />
      )}
    </Flex>
  );
};

export default SelectionItem;
